import { useEffect, useState } from 'react';
import isBrowser from '../../util/isBrowser';
import storage from '../../util/storage';

/**
 * Persisted boolean debug prefs. Storage: the key as given.
 * Dispatches `wsl-debug-flag-change` with `{ key, enabled }` on change.
 */
export const DebugFlags = {
  /** Highlight `[data-variant]` controls. Default off. */
  ButtonVariant: 'wsl_debug_panel_button_variant',
  /** Show Universal section debug chrome. Default on. */
  SectionDebug: 'wsl_debug_section_debug',
} as const;

export type DebugFlag = (typeof DebugFlags)[keyof typeof DebugFlags];

/** Flags that are enabled when storage has no value. */
const DEFAULT_ON = new Set<DebugFlag>([DebugFlags.SectionDebug]);

const CHANGE_EVENT = 'wsl-debug-flag-change';

type DebugFlagChangeDetail = {
  key: string;
  enabled: boolean;
};

function defaultEnabled(key: DebugFlag): boolean {
  return DEFAULT_ON.has(key);
}

export function isDebugFlagEnabled(key: DebugFlag): boolean {
  if (!isBrowser) {
    return defaultEnabled(key);
  }
  const raw = storage.getItem(key);
  if (raw === null) {
    return defaultEnabled(key);
  }
  return raw === 'true';
}

export function setDebugFlag(key: DebugFlag, enabled: boolean) {
  if (!isBrowser) {
    return;
  }
  // Persist only when departing from the default so defaults stay storage-free.
  if (enabled === defaultEnabled(key)) {
    storage.removeItem(key);
  } else {
    storage.setItem(key, enabled ? 'true' : 'false');
  }
  window.dispatchEvent(
    new CustomEvent<DebugFlagChangeDetail>(CHANGE_EVENT, {
      detail: { key, enabled },
    })
  );
}

export function toggleDebugFlag(key: DebugFlag): boolean {
  const next = !isDebugFlagEnabled(key);
  setDebugFlag(key, next);
  return next;
}

export function subscribeDebugFlag(
  key: DebugFlag,
  onChange: (enabled: boolean) => void
): () => void {
  if (!isBrowser) {
    return () => {};
  }
  const handler = (event: Event) => {
    const { detail } = event as CustomEvent<DebugFlagChangeDetail>;
    if (detail?.key === key) {
      onChange(detail.enabled);
    }
  };
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** React subscription for a persisted debug flag. */
export function useDebugFlag(key: DebugFlag): boolean {
  const [enabled, setEnabled] = useState(() =>
    isBrowser ? isDebugFlagEnabled(key) : defaultEnabled(key)
  );

  useEffect(() => {
    if (!isBrowser) {
      return undefined;
    }
    setEnabled(isDebugFlagEnabled(key));
    return subscribeDebugFlag(key, setEnabled);
  }, [key]);

  return enabled;
}
